import axios from "axios";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authClient = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30000,
});

const AuthContext = createContext(null);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  // null = checking, object = signed in, false = signed out
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authClient.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 is expected when not signed in; only log unexpected errors
      if (e?.response?.status && e.response.status !== 401) {
        console.warn("[auth] refresh failed:", e);
      }
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await authClient.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await authClient.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await authClient.post("/auth/logout");
    } catch (e) {
      console.warn("[auth] logout request failed:", e);
    }
    setUser(false);
  };

  const forgotPassword = async (email) =>
    authClient.post("/auth/forgot-password", { email }).then((r) => r.data);

  const resetPassword = async (token, new_password) =>
    authClient
      .post("/auth/reset-password", { token, new_password })
      .then((r) => r.data);

  return (
    <AuthContext.Provider
      value={useMemo(
        () => ({
          user,
          login,
          register,
          logout,
          refresh,
          forgotPassword,
          resetPassword,
          client: authClient,
        }),
        // Action functions are recreated each render but only close over
        // setUser (stable) — rebuilding the memo when `user` or `refresh`
        // change is enough to keep consumers correct.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [user, refresh],
      )}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// True when the logged-in visitor is admin or staff. Used to gate features
// like the SKU count next to categories/filters that we hide from shoppers.
export const useIsStaff = () => {
  const { user } = useAuth();
  return !!user && ["admin", "staff"].includes(user.role);
};

export { authClient };
